import React, { useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Area, AreaChart, RadialBar, RadialBarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import { useAuth } from '../contexts/AuthContext';
import api from '../utils/api';

const GRADE_COLORS = {
  A: 'var(--neon-green)',
  B: 'var(--neon-cyan)',
  C: 'var(--neon-yellow)',
  D: 'var(--neon-pink)',
};

const MODULE_CONFIG = {
  emissions: { label: 'Measure Emissions', link: '/emissions', icon: '📊' },
  logistics:  { label: 'Low-Carbon Logistics',  link: '/services/logistics', icon: '🚛' },
  packaging:  { label: 'Circular Packaging',  link: '/services/packaging', icon: '📦' },
  waste:      { label: 'Waste Management', link: '/services/waste',     icon: '♻️' },
  procurement:{ label: 'Procurement',      link: '/services/procurement', icon: '🛒' },
};

export default function Dashboard() {
  const { user } = useAuth();
  const [emissionScore, setEmissionScore] = useState(null);
  const [emissionSummary, setEmissionSummary] = useState(null);
  const [recommendations, setRecommendations] = useState([]);
  const [scope3Summary, setScope3Summary] = useState(null);
  const [serviceSummary, setServiceSummary] = useState(null);
  const [portfolio, setPortfolio] = useState(null);
  const [txns, setTxns] = useState([]);
  const [prices, setPrices] = useState(null);
  const [notifs, setNotifs] = useState([]);

  useEffect(() => {
    // DaaS data
    api.get('/emissions/score').then((r) => setEmissionScore(r.data)).catch(() => {});
    api.get('/emissions/summary').then((r) => setEmissionSummary(r.data)).catch(() => {});
    api.get('/emissions/recommendations').then((r) => setRecommendations(r.data.recommendations || [])).catch(() => {});
    api.get('/supply-chain/scope3-summary').then((r) => setScope3Summary(r.data)).catch(() => {});
    api.get('/services/summary').then((r) => setServiceSummary(r.data.summary || {})).catch(() => {});
    // Carbon economy data
    api.get('/portfolio').then((r) => setPortfolio(r.data)).catch(() => {});
    api.get('/trades/my').then((r) => setTxns(r.data.transactions?.slice(0, 5) || [])).catch(() => {});
    api.get('/market/prices').then((r) => setPrices(r.data)).catch(() => {});
    api.get('/users/notifications').then((r) => setNotifs(r.data.notifications || [])).catch(() => {});
  }, []);

  const markNotificationsRead = async () => {
    try {
      await api.put('/users/notifications/read');
      setNotifs((prev) => prev.map((n) => ({ ...n, read: true })));
    } catch {}
  };

  const chartData = useMemo(() => prices?.history?.slice(-14).map((point, i) => ({
    day: `D-${14 - i}`,
    price: Math.round(point.ccc_mandatory),
  })) || [], [prices]);

  const gradeColor = emissionScore ? GRADE_COLORS[emissionScore.grade] || 'var(--neon-cyan)' : 'var(--muted2)';
  const unreadCount = notifs.filter((n) => !n.read).length;
  const userFirstName = user?.name?.split(' ')[0] || 'there';

  // Module activation status (any service log or emission log = active)
  const activeModules = {
    emissions:   !!(emissionScore),
    logistics:   !!(serviceSummary?.logistics?.log_count > 0),
    packaging:   !!(serviceSummary?.packaging?.log_count > 0),
    waste:       !!(serviceSummary?.waste?.log_count > 0),
    procurement: !!(serviceSummary?.procurement?.log_count > 0),
  };

  const latestPeriodData = useMemo(() => {
    if (!emissionSummary?.periods?.length) return null;
    return emissionSummary.periods[0];
  }, [emissionSummary]);

  const roiLabel = (months) => {
    if (months === 0) return 'Immediate';
    if (months <= 6) return `${months}mo ROI`;
    if (months <= 18) return `${months}mo ROI`;
    return `${months}mo ROI`;
  };

  const roiBadgeColor = (months) => {
    if (months === 0) return 'var(--neon-green)';
    if (months <= 6) return 'var(--neon-green)';
    if (months <= 18) return 'var(--neon-yellow)';
    return 'var(--neon-pink)';
  };

  const CATEGORY_COLORS = { energy: 'var(--neon-orange)', offsets: 'var(--neon-green)', supply: 'var(--neon-cyan)', strategy: 'var(--neon-yellow)', packaging: 'var(--neon-pink)', logistics: 'var(--neon-blue)', data: 'var(--muted2)' };

  return (
    <div className="page-shell page-stack">

      {/* ── Hero greeting ── */}
      <section className="dashboard-hero">
        <div className="surface-panel dashboard-summary glow-surface">
          <div className="dashboard-highlight">
            <div className="section-stack">
              <div className="section-tag">CarbonX OS · Decarbonization Dashboard</div>
              <h1>
                Welcome back,
                <span style={{ color: 'var(--neon-green)' }}> {userFirstName}</span>
              </h1>
              <p className="section-copy" style={{ maxWidth: '560px' }}>
                Measure your emissions, plan reduction, execute with green services, and offset the rest.
              </p>
            </div>
            <div className="inline-actions">
              <Link to="/emissions" className="btn btn-primary">Log emissions</Link>
              <Link to="/roadmap" className="btn btn-secondary">View roadmap</Link>
            </div>
          </div>
          <div className="summary-strip">
            <div className="summary-tile">
              <div className="stat-label">KYC status</div>
              <div className="value" style={{ color: user?.kyc_status === 'verified' ? 'var(--neon-green)' : 'var(--neon-yellow)', textTransform: 'capitalize' }}>
                {(user?.kyc_status || 'pending').replace(/_/g, ' ')}
              </div>
            </div>
            <div className="summary-tile">
              <div className="stat-label">GCI ID</div>
              <div className="value" style={{ color: 'var(--neon-cyan)', fontSize: '1rem' }}>{user?.gci_id || 'Pending'}</div>
            </div>
            <div className="summary-tile">
              <div className="stat-label">Alerts</div>
              <div className="value" style={{ color: unreadCount > 0 ? 'var(--neon-green)' : 'var(--muted2)' }}>{unreadCount}</div>
            </div>
          </div>
        </div>

        {/* Carbon Score spotlight */}
        <div className="surface-panel spotlight-card" style={{ borderTop: `3px solid ${gradeColor}` }}>
          <div className="section-tag" style={{ color: gradeColor }}>Carbon Score</div>
          {emissionScore ? (
            <>
              <div className="spotlight-value" style={{ color: gradeColor }}>{emissionScore.score}<span style={{ fontSize: '1.4rem', color: 'var(--muted2)' }}>/100</span></div>
              <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap', margin: '0.5rem 0' }}>
                <span className="badge" style={{ color: gradeColor, borderColor: gradeColor }}>Grade {emissionScore.grade}</span>
                <span className="badge" style={{ color: 'var(--muted2)' }}>{emissionScore.data_completeness_pct}% complete</span>
              </div>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '0.75rem', marginTop: '0.75rem' }}>
                <div>
                  <div className="stat-label">Gross emissions</div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.85rem', color: 'var(--neon-orange)' }}>{emissionScore.gross_emissions_tco2e} tCO2e</div>
                </div>
                <div>
                  <div className="stat-label">Net emissions</div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.85rem', color: gradeColor }}>{emissionScore.net_emissions_tco2e} tCO2e</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="spotlight-value" style={{ color: 'var(--muted2)' }}>—</div>
              <div className="spotlight-copy">No score yet</div>
            </>
          )}
          <Link to="/emissions" className="btn btn-secondary" style={{ marginTop: 'auto', borderColor: gradeColor, color: gradeColor }}>View details</Link>
        </div>
      </section>

      {/* ── DaaS Action Cards ── */}
      <section className="stats-grid">
        <Link to="/emissions" style={{ textDecoration: 'none' }}>
          <div className="surface-panel stat-card">
            <div className="stat-label">Measure</div>
            <div className="stat-value" style={{ color: 'var(--neon-green)' }}>
              {emissionSummary?.periods?.[0]?.log_count || 0} <span style={{ fontSize: '1rem' }}>logs</span>
            </div>
            <div className="stat-sub">this period · Scope 1/2/3 tracked</div>
          </div>
        </Link>
        <Link to="/services/logistics" style={{ textDecoration: 'none' }}>
          <div className="surface-panel stat-card">
            <div className="stat-label">Reduce</div>
            <div className="stat-value" style={{ color: 'var(--neon-cyan)' }}>
              {Object.values(serviceSummary || {}).filter((m) => m.active).length} <span style={{ fontSize: '1rem' }}>active</span>
            </div>
            <div className="stat-sub">green service modules running</div>
          </div>
        </Link>
        <Link to="/roadmap" style={{ textDecoration: 'none' }}>
          <div className="surface-panel stat-card">
            <div className="stat-label">Plan</div>
            <div className="stat-value" style={{ color: 'var(--neon-orange)' }}>
              {emissionScore ? `${emissionScore.score}/100` : '—'}
            </div>
            <div className="stat-sub">decarbonization score · view roadmap</div>
          </div>
        </Link>
        <Link to="/listings" style={{ textDecoration: 'none' }}>
          <div className="surface-panel stat-card">
            <div className="stat-label">Offset</div>
            <div className="stat-value" style={{ color: 'var(--neon-pink)' }}>
              {(user?.carbon_balance || 0).toLocaleString('en-IN')} <span style={{ fontSize: '1rem' }}>tCO2e</span>
            </div>
            <div className="stat-sub">carbon credits held · browse market</div>
          </div>
        </Link>
      </section>

      {/* ── DaaS Module Status Pills ── */}
      <section className="surface-panel" style={{ padding: '1.25rem 1.5rem' }}>
        <div className="section-header-row" style={{ marginBottom: '1rem' }}>
          <div className="section-tag">Platform modules</div>
          <span className="badge" style={{ color: 'var(--muted2)' }}>CarbonX OS</span>
        </div>
        <div style={{ display: 'flex', gap: '0.75rem', flexWrap: 'wrap' }}>
          {Object.entries(MODULE_CONFIG).map(([key, cfg]) => {
            const isActive = activeModules[key];
            return (
              <Link key={key} to={cfg.link} style={{ textDecoration: 'none' }}>
                <div style={{
                  display: 'flex', alignItems: 'center', gap: '0.5rem',
                  padding: '0.5rem 1rem', borderRadius: '999px',
                  border: `1px solid ${isActive ? 'var(--neon-green)' : 'var(--border)'}`,
                  background: isActive ? 'rgba(0,255,136,0.06)' : 'transparent',
                  cursor: 'pointer',
                }}>
                  <span style={{ fontSize: '0.9rem' }}>{cfg.icon}</span>
                  <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.72rem', color: isActive ? 'var(--neon-green)' : 'var(--muted2)' }}>
                    {isActive ? '✓ ' : '○ '}{cfg.label}
                  </span>
                  {!isActive && <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.62rem', color: 'var(--neon-cyan)' }}>Get started →</span>}
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* ── Scope 1/2/3 Breakdown Bar ── */}
      {latestPeriodData && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1.25rem' }}>
            <div className="section-stack">
              <div className="section-tag">Emissions breakdown</div>
              <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontWeight: 700, fontSize: '1rem' }}>
                {latestPeriodData.period} — {latestPeriodData.total} tCO2e total
              </div>
            </div>
            <Link to="/emissions" className="btn btn-secondary btn-sm">View tracker</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '1.5rem' }}>
            {[
              { label: 'Scope 1 (Direct)', value: latestPeriodData.scope1, color: 'var(--neon-orange)', desc: 'Combustion, mobile, fugitive' },
              { label: 'Scope 2 (Electricity)', value: latestPeriodData.scope2, color: 'var(--neon-yellow)', desc: 'Purchased electricity & heat' },
              { label: 'Scope 3 (Value chain)', value: latestPeriodData.scope3, color: 'var(--neon-cyan)', desc: 'Logistics, suppliers, travel' },
            ].map((s) => (
              <div key={s.label}>
                <div className="stat-label" style={{ marginBottom: '0.35rem' }}>{s.label}</div>
                <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '1.4rem', fontWeight: 700, color: s.color }}>
                  {s.value} <span style={{ fontSize: '0.75rem', color: 'var(--muted2)' }}>tCO2e</span>
                </div>
                <div style={{ height: '4px', borderRadius: '2px', background: 'var(--border)', marginTop: '0.5rem', overflow: 'hidden' }}>
                  <div style={{
                    height: '100%', borderRadius: '2px', background: s.color,
                    width: latestPeriodData.total > 0 ? `${Math.round((s.value / latestPeriodData.total) * 100)}%` : '0%',
                  }} />
                </div>
                <div className="stat-sub" style={{ marginTop: '0.3rem' }}>{s.desc}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Recommendations ── */}
      {recommendations.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1.25rem' }}>
            <div className="section-stack">
              <div className="section-tag">AI Recommendations</div>
              <h2 style={{ fontSize: '1.2rem', letterSpacing: '-0.02em' }}>Top reduction actions for you</h2>
            </div>
            <Link to="/emissions" className="btn btn-secondary btn-sm">All insights</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))', gap: '1rem' }}>
            {recommendations.slice(0, 3).map((rec, i) => (
              <Link key={i} to={rec.link} style={{ textDecoration: 'none' }}>
                <div className="surface-panel" style={{
                  padding: '1.25rem', borderLeft: `3px solid ${CATEGORY_COLORS[rec.category] || 'var(--neon-cyan)'}`,
                  cursor: 'pointer', transition: 'background 0.2s',
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '0.75rem' }}>
                    <span className="badge" style={{ color: CATEGORY_COLORS[rec.category] || 'var(--neon-cyan)', borderColor: CATEGORY_COLORS[rec.category] || 'var(--neon-cyan)', textTransform: 'capitalize', fontSize: '0.6rem' }}>
                      {rec.category}
                    </span>
                    <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.62rem', color: roiBadgeColor(rec.roi_months), padding: '0.2rem 0.5rem', border: `1px solid ${roiBadgeColor(rec.roi_months)}`, borderRadius: '999px' }}>
                      {roiLabel(rec.roi_months)}
                    </span>
                  </div>
                  <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.85rem', fontWeight: 600, color: 'var(--text)', lineHeight: 1.45 }}>
                    {rec.text}
                  </div>
                  <div style={{ marginTop: '0.75rem', fontFamily: 'Space Mono, monospace', fontSize: '0.65rem', color: 'var(--neon-cyan)' }}>Take action →</div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      {/* ── Scope 3 Supplier Hotspot ── */}
      {scope3Summary && scope3Summary.supplier_count > 0 && (
        <section className="surface-panel" style={{ padding: '1.5rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1rem' }}>
            <div className="section-stack">
              <div className="section-tag">Supply chain</div>
              <h2 style={{ fontSize: '1.1rem', letterSpacing: '-0.02em' }}>
                Scope 3: {scope3Summary.total_scope3_tco2e} tCO2e from {scope3Summary.supplier_count} suppliers
              </h2>
            </div>
            <Link to="/supply-chain" className="btn btn-secondary btn-sm">Manage suppliers</Link>
          </div>
          <div style={{ display: 'grid', gap: '0.6rem' }}>
            {scope3Summary.top3_suppliers.map((s, i) => (
              <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr auto auto', gap: '1rem', alignItems: 'center', padding: '0.75rem 1rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px' }}>
                <span style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.88rem', color: 'var(--text)' }}>{s.supplier_name}</span>
                <span style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.78rem', color: 'var(--neon-orange)' }}>{s.estimated_scope3_tco2e} tCO2e</span>
                <span className="badge" style={{ color: GRADE_COLORS[s.emissions_grade] || 'var(--muted2)', borderColor: GRADE_COLORS[s.emissions_grade] || 'var(--border)' }}>
                  Grade {s.emissions_grade || '?'}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Carbon Economy (de-emphasized) ── */}
      <section className="section-grid-2">
        <div className="surface-panel" style={{ padding: '1.4rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1rem' }}>
            <div className="section-stack">
              <div className="section-tag">Carbon economy</div>
              <h2 style={{ fontSize: '1.2rem', letterSpacing: '-0.02em' }}>Portfolio & trading</h2>
            </div>
            <Link to="/portfolio" className="btn btn-secondary btn-sm">Full portfolio</Link>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
            {[
              { label: 'INR balance', value: `₹${(user?.balance || 0).toLocaleString('en-IN')}`, color: 'var(--neon-green)', link: '/wallet' },
              { label: 'Carbon credits', value: `${(user?.carbon_balance || 0).toLocaleString('en-IN')} tCO2e`, color: 'var(--neon-cyan)', link: '/portfolio' },
              { label: 'Portfolio value', value: portfolio ? `₹${(portfolio.summary?.total_value || 0).toLocaleString('en-IN')}` : '—', color: 'var(--neon-orange)', link: '/portfolio' },
              { label: 'Market ref (CCC)', value: `₹${Math.round(prices?.spot?.ccc_mandatory || 0).toLocaleString('en-IN')}`, color: 'var(--neon-yellow)', link: '/market/india' },
            ].map((item) => (
              <Link key={item.label} to={item.link} style={{ textDecoration: 'none' }}>
                <div style={{ padding: '0.85rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', border: '1px solid var(--border)' }}>
                  <div className="stat-label">{item.label}</div>
                  <div style={{ fontFamily: 'Space Mono, monospace', fontSize: '0.88rem', color: item.color, marginTop: '0.3rem' }}>{item.value}</div>
                </div>
              </Link>
            ))}
          </div>
          <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1rem' }}>
            <Link to="/listings" className="btn btn-secondary btn-sm" style={{ flex: 1, textAlign: 'center' }}>Buy credits</Link>
            <Link to="/trade" className="btn btn-secondary btn-sm" style={{ flex: 1, textAlign: 'center' }}>Trade desk</Link>
          </div>
        </div>

        <div className="surface-panel" style={{ padding: '1.4rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1rem' }}>
            <div className="section-stack">
              <div className="section-tag">Notifications</div>
              <h2 style={{ fontSize: '1.2rem', letterSpacing: '-0.02em' }}>Alerts</h2>
            </div>
            {unreadCount > 0 && (
              <button type="button" className="btn btn-secondary btn-sm" onClick={markNotificationsRead}>Mark read</button>
            )}
          </div>
          <div className="section-stack thin-scroll" style={{ maxHeight: '260px', overflow: 'auto', gap: '0.5rem' }}>
            {notifs.length === 0 ? (
              <div className="stat-sub">No notifications yet.</div>
            ) : notifs.slice(0, 8).map((item, i) => (
              <div key={i} style={{ padding: '0.75rem', background: 'rgba(255,255,255,0.02)', borderRadius: '8px', borderLeft: `2px solid ${item.read ? 'var(--border)' : 'var(--neon-green)'}` }}>
                <div style={{ fontFamily: '"Plus Jakarta Sans", sans-serif', fontSize: '0.82rem', color: 'var(--text)', lineHeight: 1.4 }}>{item.message}</div>
                <div className="stat-label" style={{ marginTop: '0.3rem' }}>{new Date(item.created_at).toLocaleDateString('en-IN')}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── 14-day CCC Price Chart ── */}
      {chartData.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.4rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1rem' }}>
            <div className="section-stack">
              <div className="section-tag">Reference trend</div>
              <h2 style={{ fontSize: '1.2rem', letterSpacing: '-0.02em' }}>CCC Mandatory price — last 14 days</h2>
            </div>
            <div className="live-indicator"><div className="live-dot" />Snapshot</div>
          </div>
          <ResponsiveContainer width="100%" height={200}>
            <AreaChart data={chartData}>
              <defs>
                <linearGradient id="priceGrad" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#00ff88" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#00ff88" stopOpacity={0} />
                </linearGradient>
              </defs>
              <XAxis dataKey="day" tick={{ fontFamily: 'Space Mono', fontSize: 9, fill: '#5a5e72' }} />
              <YAxis tick={{ fontFamily: 'Space Mono', fontSize: 9, fill: '#5a5e72' }} domain={['auto', 'auto']} />
              <Tooltip contentStyle={{ background: '#0d0f1a', border: '1px solid rgba(255,255,255,0.1)', fontFamily: 'Space Mono', fontSize: '0.7rem', borderRadius: '8px' }} />
              <Area type="monotone" dataKey="price" stroke="#00ff88" fill="url(#priceGrad)" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </section>
      )}

      {/* ── Recent Transactions ── */}
      {txns.length > 0 && (
        <section className="surface-panel" style={{ padding: '1.4rem' }}>
          <div className="section-header-row" style={{ marginBottom: '1rem' }}>
            <div className="section-stack">
              <div className="section-tag">Activity</div>
              <h2 style={{ fontSize: '1.2rem', letterSpacing: '-0.02em' }}>Recent transactions</h2>
            </div>
            <Link to="/transactions" className="btn btn-secondary btn-sm">View all</Link>
          </div>
          <table className="data-table">
            <thead>
              <tr><th>Date</th><th>Type</th><th>Credits</th><th>Total</th><th>Status</th></tr>
            </thead>
            <tbody>
              {txns.map((t) => (
                <tr key={t.id}>
                  <td style={{ color: 'var(--muted2)' }}>{new Date(t.created_at).toLocaleDateString('en-IN')}</td>
                  <td><span className={`badge badge-${t.type === 'BUY' ? 'green' : t.type === 'EPA_SURRENDER' ? 'orange' : 'pink'}`}>{t.type}</span></td>
                  <td>{t.qty?.toLocaleString('en-IN')} tCO2e</td>
                  <td style={{ color: 'var(--neon-green)' }}>{t.total ? `₹${t.total.toLocaleString('en-IN')}` : '—'}</td>
                  <td><span className="badge badge-cyan">{t.status}</span></td>
                </tr>
              ))}
            </tbody>
          </table>
        </section>
      )}
    </div>
  );
}
